import React, { useState, useEffect, useCallback } from 'react';
import { useSite } from '@/contexts/SiteContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Search,
  Loader2,
  Wrench,
  ArrowRight,
  Undo2,
  History,
  Plus,
  Trash2,
  Play,
  Pause,
  CheckCircle2,
  AlertCircle,
  XCircle,
  ExternalLink,
  ChevronDown,
  ChevronRight,
  RefreshCw,
  Eye,
  Edit3,
  Sparkles,
  FileText,
  Package,
  Upload
} from 'lucide-react';
import { authFetch } from '@/utils/auth-fetch';
import { API_URL } from '@/config/api';
import { BulkUploadTab } from '@/components/enrichment/BulkUploadTab';

interface Product {
  id: string;
  name: string;
  brand: string;
  category: string;
  productUrl?: string;
  attributes: Record<string, unknown>;
}

interface SearchResult {
  interpretation: Record<string, unknown>;
  total: number;
  products: Product[];
  tokensUsed: number;
}

interface PreviewChange {
  type: 'added' | 'changed' | 'removed';
  attribute: string;
  from?: string;
  to?: string;
  value?: string;
}

interface PreviewResult {
  id: string;
  name: string;
  before: Record<string, unknown>;
  after: Record<string, unknown>;
  changes: PreviewChange[];
}

interface UpdateHistory {
  id: string;
  query: string;
  changes: string;
  productsMatched: number;
  productsModified: number;
  status: string;
  createdAt: string;
  completedAt?: string;
  userName: string;
  undoneAt?: string;
  undoneByName?: string;
  canUndo: boolean;
}

interface CorrectionRule {
  id: string;
  name: string;
  naturalLanguage: string;
  conditions: Record<string, unknown>;
  actions: Array<{ type: string; attribute: string; value?: string }>;
  applyTo: { feed_import: boolean; enrichment: boolean; manual: boolean };
  status: string;
  createdAt: string;
  createdBy: string;
  applications: number;
  lastApplied?: string;
}

export default function BulkCorrection() {
  const { selectedSite } = useSite();
  const [activeTab, setActiveTab] = useState('search');

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult | null>(null);
  const [selectedProducts, setSelectedProducts] = useState<Set<string>>(new Set());
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  // Update state
  const [changeDescription, setChangeDescription] = useState('');
  const [previewResults, setPreviewResults] = useState<PreviewResult[] | null>(null);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);
  const [triggerRegeneration, setTriggerRegeneration] = useState(false);
  const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
  const [lastUpdateResult, setLastUpdateResult] = useState<{ updateId: string; productsModified: number } | null>(null);

  // History state
  const [history, setHistory] = useState<UpdateHistory[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  // Rules state
  const [rules, setRules] = useState<CorrectionRule[]>([]);
  const [isLoadingRules, setIsLoadingRules] = useState(false);
  const [newRuleDialogOpen, setNewRuleDialogOpen] = useState(false);
  const [newRuleName, setNewRuleName] = useState('');
  const [newRuleText, setNewRuleText] = useState('');
  const [newRuleApplyTo, setNewRuleApplyTo] = useState({ feed_import: true, enrichment: true, manual: false });
  const [isCreatingRule, setIsCreatingRule] = useState(false);
  const [ruleTestResult, setRuleTestResult] = useState<{ matchCount: number; samples: PreviewResult[] } | null>(null);
  const [isTestingRule, setIsTestingRule] = useState(false);

  // Load history and rules when site changes
  useEffect(() => {
    if (selectedSite?.id) {
      loadHistory();
      loadRules();
    }
  }, [selectedSite?.id]);

  const loadHistory = async () => {
    if (!selectedSite?.id) return;
    setIsLoadingHistory(true);
    try {
      const response = await authFetch(`${API_URL}/bulk-correction/history?siteId=${selectedSite.id}`);
      const data = await response.json();
      if (data.success) {
        setHistory(data.data);
      }
    } catch (error) {
      console.error('Failed to load history:', error);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const loadRules = async () => {
    if (!selectedSite?.id) return;
    setIsLoadingRules(true);
    try {
      const response = await authFetch(`${API_URL}/bulk-correction/rules?siteId=${selectedSite.id}`);
      const data = await response.json();
      if (data.success) {
        setRules(data.data);
      }
    } catch (error) {
      console.error('Failed to load rules:', error);
    } finally {
      setIsLoadingRules(false);
    }
  };

  const handleSearch = async () => {
    if (!selectedSite?.id || !searchQuery.trim()) return;

    setIsSearching(true);
    setSearchError(null);
    setSearchResults(null);
    setSelectedProducts(new Set());

    try {
      const response = await authFetch(`${API_URL}/bulk-correction/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          siteId: selectedSite.id,
          query: searchQuery,
          limit: 100
        })
      });

      const data = await response.json();
      if (data.success) {
        setSearchResults(data.data);
        // Select all by default
        setSelectedProducts(new Set(data.data.products.map((p: Product) => p.id)));
      } else {
        setSearchError(data.error || 'Search failed');
      }
    } catch (error) {
      setSearchError('Failed to search products');
      console.error('Search error:', error);
    } finally {
      setIsSearching(false);
    }
  };

  const handlePreview = async () => {
    if (!selectedSite?.id || !changeDescription.trim() || selectedProducts.size === 0) return;

    setIsPreviewing(true);
    setPreviewResults(null);

    try {
      const response = await authFetch(`${API_URL}/bulk-correction/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          siteId: selectedSite.id,
          productIds: Array.from(selectedProducts),
          changes: changeDescription
        })
      });

      const data = await response.json();
      if (data.success) {
        setPreviewResults(data.data.previews);
        setUpdateDialogOpen(true);
      }
    } catch (error) {
      console.error('Preview error:', error);
    } finally {
      setIsPreviewing(false);
    }
  };

  const handleExecute = async () => {
    if (!selectedSite?.id || !changeDescription.trim() || selectedProducts.size === 0) return;

    setIsExecuting(true);

    try {
      const response = await authFetch(`${API_URL}/bulk-correction/execute`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          siteId: selectedSite.id,
          productIds: Array.from(selectedProducts),
          changes: changeDescription,
          triggerRegeneration
        })
      });

      const data = await response.json();
      if (data.success) {
        setLastUpdateResult({
          updateId: data.data.updateId,
          productsModified: data.data.productsModified
        });
        setUpdateDialogOpen(false);
        setChangeDescription('');
        setSearchResults(null);
        setSelectedProducts(new Set());
        loadHistory();
      }
    } catch (error) {
      console.error('Execute error:', error);
    } finally {
      setIsExecuting(false);
    }
  };

  const handleUndo = async (updateId: string) => {
    try {
      const response = await authFetch(`${API_URL}/bulk-correction/undo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updateId })
      });

      const data = await response.json();
      if (data.success) {
        loadHistory();
      }
    } catch (error) {
      console.error('Undo error:', error);
    }
  };

  const handleTestRule = async () => {
    if (!selectedSite?.id || !newRuleText.trim()) return;

    setIsTestingRule(true);
    setRuleTestResult(null);

    try {
      const response = await authFetch(`${API_URL}/bulk-correction/rules/test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          siteId: selectedSite.id,
          naturalLanguage: newRuleText
        })
      });

      const data = await response.json();
      if (data.success) {
        setRuleTestResult({
          matchCount: data.data.matchCount,
          samples: data.data.samples
        });
      }
    } catch (error) {
      console.error('Test rule error:', error);
    } finally {
      setIsTestingRule(false);
    }
  };

  const handleCreateRule = async () => {
    if (!selectedSite?.id || !newRuleName.trim() || !newRuleText.trim()) return;

    setIsCreatingRule(true);

    try {
      const response = await authFetch(`${API_URL}/bulk-correction/rules`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          siteId: selectedSite.id,
          name: newRuleName,
          naturalLanguage: newRuleText,
          applyTo: newRuleApplyTo
        })
      });

      const data = await response.json();
      if (data.success) {
        setNewRuleDialogOpen(false);
        setNewRuleName('');
        setNewRuleText('');
        setRuleTestResult(null);
        loadRules();
      }
    } catch (error) {
      console.error('Create rule error:', error);
    } finally {
      setIsCreatingRule(false);
    }
  };

  const handleToggleRuleStatus = async (ruleId: string, currentStatus: string) => {
    const newStatus = currentStatus === 'active' ? 'disabled' : 'active';

    try {
      const response = await authFetch(`${API_URL}/bulk-correction/rules/${ruleId}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });

      const data = await response.json();
      if (data.success) {
        loadRules();
      }
    } catch (error) {
      console.error('Toggle rule status error:', error);
    }
  };

  const handleDeleteRule = async (ruleId: string) => {
    if (!confirm('Are you sure you want to delete this rule?')) return;

    try {
      const response = await authFetch(`${API_URL}/bulk-correction/rules/${ruleId}`, {
        method: 'DELETE'
      });

      const data = await response.json();
      if (data.success) {
        loadRules();
      }
    } catch (error) {
      console.error('Delete rule error:', error);
    }
  };

  const toggleProductSelection = (productId: string) => {
    const newSelected = new Set(selectedProducts);
    if (newSelected.has(productId)) {
      newSelected.delete(productId);
    } else {
      newSelected.add(productId);
    }
    setSelectedProducts(newSelected);
  };

  const toggleAllProducts = () => {
    if (!searchResults) return;
    if (selectedProducts.size === searchResults.products.length) {
      setSelectedProducts(new Set());
    } else {
      setSelectedProducts(new Set(searchResults.products.map(p => p.id)));
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-green-100 text-green-800"><CheckCircle2 className="h-3 w-3 mr-1" />Completed</Badge>;
      case 'running':
        return <Badge className="bg-blue-100 text-blue-800"><Loader2 className="h-3 w-3 mr-1 animate-spin" />Running</Badge>;
      case 'failed':
        return <Badge className="bg-red-100 text-red-800"><XCircle className="h-3 w-3 mr-1" />Failed</Badge>;
      case 'undone':
        return <Badge className="bg-gray-100 text-gray-800"><Undo2 className="h-3 w-3 mr-1" />Undone</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  if (!selectedSite) {
    return (
      <div className="p-6">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Please select a site to use bulk correction.</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Wrench className="h-6 w-6" />
            Bulk Data Correction
          </h1>
          <p className="text-muted-foreground mt-1">
            Search products with natural language and apply bulk corrections
          </p>
        </div>
      </div>

      {lastUpdateResult && (
        <Alert className="bg-green-50 border-green-200">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-800">
            Successfully updated {lastUpdateResult.productsModified} products.
          </AlertDescription>
        </Alert>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="search" className="flex items-center gap-2">
            <Search className="h-4 w-4" />
            Search & Update
          </TabsTrigger>
          <TabsTrigger value="rules" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Correction Rules
          </TabsTrigger>
          <TabsTrigger value="history" className="flex items-center gap-2">
            <History className="h-4 w-4" />
            History
          </TabsTrigger>
          <TabsTrigger value="upload" className="flex items-center gap-2">
            <Upload className="h-4 w-4" />
            Upload CSV
          </TabsTrigger>
        </TabsList>

        {/* Search & Update Tab */}
        <TabsContent value="search" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="h-5 w-5" />
                Natural Language Search
              </CardTitle>
              <CardDescription>
                Describe the products you want to find using natural language
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Input
                  placeholder='e.g., "all bicycle tires with diameter 28 inches" or "Continental tires missing puncture protection"'
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  className="flex-1"
                />
                <Button onClick={handleSearch} disabled={isSearching || !searchQuery.trim()}>
                  {isSearching ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Search className="h-4 w-4" />
                  )}
                  <span className="ml-2">Search</span>
                </Button>
              </div>

              {searchError && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{searchError}</AlertDescription>
                </Alert>
              )}

              {searchResults && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <span className="text-sm text-muted-foreground">
                        Found <span className="font-semibold">{searchResults.total}</span> products
                      </span>
                      {searchResults.interpretation && (
                        <Badge variant="outline" className="text-xs">
                          Interpreted: {JSON.stringify(searchResults.interpretation).slice(0, 50)}...
                        </Badge>
                      )}
                    </div>
                    <Button variant="outline" size="sm" onClick={toggleAllProducts}>
                      {selectedProducts.size === searchResults.products.length ? 'Deselect All' : 'Select All'}
                    </Button>
                  </div>

                  <ScrollArea className="h-[300px] border rounded-lg">
                    <div className="p-2 space-y-1">
                      {searchResults.products.map((product) => (
                        <div
                          key={product.id}
                          className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer hover:bg-muted/50 ${
                            selectedProducts.has(product.id) ? 'bg-muted' : ''
                          }`}
                          onClick={() => toggleProductSelection(product.id)}
                        >
                          <Checkbox
                            checked={selectedProducts.has(product.id)}
                            onCheckedChange={() => toggleProductSelection(product.id)}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="font-medium truncate">{product.name}</div>
                            <div className="text-sm text-muted-foreground flex items-center gap-2">
                              {product.brand && <span>{product.brand}</span>}
                              {product.category && <span className="text-xs">| {product.category}</span>}
                            </div>
                          </div>
                          {product.productUrl && (
                            <Button variant="ghost" size="sm" asChild>
                              <a href={product.productUrl} target="_blank" rel="noopener noreferrer">
                                <ExternalLink className="h-3 w-3" />
                              </a>
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  </ScrollArea>

                  <Separator />

                  <div className="space-y-4">
                    <Label>What changes do you want to make?</Label>
                    <Textarea
                      placeholder='e.g., "set diameter = 700C and remove rim_width" or "add puncture_protection = high"'
                      value={changeDescription}
                      onChange={(e) => setChangeDescription(e.target.value)}
                      rows={3}
                    />

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Switch
                          id="trigger-regen"
                          checked={triggerRegeneration}
                          onCheckedChange={setTriggerRegeneration}
                        />
                        <Label htmlFor="trigger-regen" className="text-sm">
                          Trigger AEO regeneration after update
                        </Label>
                      </div>

                      <Button
                        onClick={handlePreview}
                        disabled={isPreviewing || !changeDescription.trim() || selectedProducts.size === 0}
                      >
                        {isPreviewing ? (
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        ) : (
                          <Eye className="h-4 w-4 mr-2" />
                        )}
                        Preview Changes ({selectedProducts.size} products)
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Correction Rules Tab */}
        <TabsContent value="rules" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Correction Rules</CardTitle>
                <CardDescription>
                  Define rules that automatically correct data during import and enrichment
                </CardDescription>
              </div>
              <Dialog open={newRuleDialogOpen} onOpenChange={setNewRuleDialogOpen}>
                <DialogTrigger asChild>
                  <Button>
                    <Plus className="h-4 w-4 mr-2" />
                    Create Rule
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>Create Correction Rule</DialogTitle>
                    <DialogDescription>
                      Define a rule in natural language that will be applied automatically
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label>Rule Name</Label>
                      <Input
                        placeholder="e.g., Fix 28 inch diameter for bicycle tires"
                        value={newRuleName}
                        onChange={(e) => setNewRuleName(e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Rule Description (Natural Language)</Label>
                      <Textarea
                        placeholder='e.g., "When a bicycle tire has diameter = 28 inches, change diameter to 700C and remove rim_width"'
                        value={newRuleText}
                        onChange={(e) => setNewRuleText(e.target.value)}
                        rows={3}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Apply To</Label>
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                          <Checkbox
                            id="apply-feed"
                            checked={newRuleApplyTo.feed_import}
                            onCheckedChange={(checked) =>
                              setNewRuleApplyTo(prev => ({ ...prev, feed_import: !!checked }))
                            }
                          />
                          <Label htmlFor="apply-feed" className="text-sm">Feed Import</Label>
                        </div>
                        <div className="flex items-center gap-2">
                          <Checkbox
                            id="apply-enrichment"
                            checked={newRuleApplyTo.enrichment}
                            onCheckedChange={(checked) =>
                              setNewRuleApplyTo(prev => ({ ...prev, enrichment: !!checked }))
                            }
                          />
                          <Label htmlFor="apply-enrichment" className="text-sm">AI Enrichment</Label>
                        </div>
                      </div>
                    </div>

                    {newRuleText && (
                      <div className="space-y-2">
                        <Button
                          variant="outline"
                          onClick={handleTestRule}
                          disabled={isTestingRule}
                        >
                          {isTestingRule ? (
                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                          ) : (
                            <Play className="h-4 w-4 mr-2" />
                          )}
                          Test Rule
                        </Button>

                        {ruleTestResult && (
                          <Alert>
                            <AlertDescription>
                              Rule would match <strong>{ruleTestResult.matchCount}</strong> products
                            </AlertDescription>
                          </Alert>
                        )}
                      </div>
                    )}
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setNewRuleDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button
                      onClick={handleCreateRule}
                      disabled={isCreatingRule || !newRuleName.trim() || !newRuleText.trim()}
                    >
                      {isCreatingRule && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                      Create Rule
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              {isLoadingRules ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : rules.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No correction rules defined yet.</p>
                  <p className="text-sm">Create a rule to automatically correct data during import.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {rules.map((rule) => (
                    <div
                      key={rule.id}
                      className="flex items-center justify-between p-4 border rounded-lg"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{rule.name}</span>
                          <Badge variant={rule.status === 'active' ? 'default' : 'secondary'}>
                            {rule.status}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1 truncate">
                          {rule.naturalLanguage}
                        </p>
                        <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                          <span>Applied {rule.applications} times</span>
                          {rule.lastApplied && (
                            <span>Last: {formatDate(rule.lastApplied)}</span>
                          )}
                          <span>Created by {rule.createdBy}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        <Switch
                          checked={rule.status === 'active'}
                          onCheckedChange={() => handleToggleRuleStatus(rule.id, rule.status)}
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteRule(rule.id)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Update History</CardTitle>
                <CardDescription>
                  View and undo previous bulk updates
                </CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={loadHistory}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
            </CardHeader>
            <CardContent>
              {isLoadingHistory ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : history.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <History className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No bulk updates yet.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {history.map((item) => (
                    <div
                      key={item.id}
                      className="flex items-center justify-between p-4 border rounded-lg"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          {getStatusBadge(item.status)}
                          <span className="font-medium truncate">{item.query}</span>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          {item.changes}
                        </p>
                        <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                          <span>{item.productsModified} of {item.productsMatched} products modified</span>
                          <span>{formatDate(item.createdAt)}</span>
                          <span>by {item.userName}</span>
                          {item.undoneAt && (
                            <span className="text-amber-600">Undone by {item.undoneByName}</span>
                          )}
                        </div>
                      </div>
                      {item.canUndo && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleUndo(item.id)}
                          className="ml-4"
                        >
                          <Undo2 className="h-4 w-4 mr-2" />
                          Undo
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Upload CSV Tab (Story 358) */}
        <TabsContent value="upload">
          <BulkUploadTab />
        </TabsContent>
      </Tabs>

      {/* Preview/Execute Dialog */}
      <Dialog open={updateDialogOpen} onOpenChange={setUpdateDialogOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Preview Changes</DialogTitle>
            <DialogDescription>
              Review the changes before applying to {selectedProducts.size} products
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {previewResults && previewResults.length > 0 ? (
              <ScrollArea className="h-[400px]">
                <div className="space-y-4">
                  {previewResults.map((preview) => (
                    <div key={preview.id} className="border rounded-lg p-4">
                      <div className="font-medium mb-2">{preview.name}</div>
                      <div className="space-y-2">
                        {preview.changes.map((change, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-sm">
                            {change.type === 'added' && (
                              <>
                                <Badge className="bg-green-100 text-green-800">ADD</Badge>
                                <span>{change.attribute} = {change.value}</span>
                              </>
                            )}
                            {change.type === 'changed' && (
                              <>
                                <Badge className="bg-blue-100 text-blue-800">SET</Badge>
                                <span>{change.attribute}: {String(change.from)} <ArrowRight className="h-3 w-3 inline" /> {change.to}</span>
                              </>
                            )}
                            {change.type === 'removed' && (
                              <>
                                <Badge className="bg-red-100 text-red-800">REMOVE</Badge>
                                <span>{change.attribute}</span>
                              </>
                            )}
                          </div>
                        ))}
                        {preview.changes.length === 0 && (
                          <span className="text-muted-foreground text-sm">No changes</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                No preview available
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUpdateDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleExecute}
              disabled={isExecuting}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {isExecuting ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Wrench className="h-4 w-4 mr-2" />
              )}
              Apply to {selectedProducts.size} Products
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
